"use client";

/* eslint-disable react/only-export-components -- preview-related helpers co-located with the previews. */

import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { FileText, FileSpreadsheet, Mail, MapPin, Globe, Code } from "lucide-react";
import { cn } from "../../../utils";
import type { CitationFileType } from "../types";
import { PreviewLoading } from "./preview-loading";

export function getFileTypeIcon(fileType: CitationFileType) {
  switch (fileType) {
    case "pdf":
      return FileText;
    case "spreadsheet":
      return FileSpreadsheet;
    case "html":
      return Code;
    case "email":
      return Mail;
    case "url":
      return Globe;
    default:
      return FileText;
  }
}

export function getFileTypeBorderClass(fileType: CitationFileType): string {
  switch (fileType) {
    case "email":
    case "url":
      return "border-blue-300 dark:border-blue-600";
    case "spreadsheet":
      return "border-green-300 dark:border-green-600";
    default:
      return "border-stone-300 dark:border-stone-600";
  }
}

export function getFileTypeTextClass(fileType: CitationFileType): string {
  if (fileType === "spreadsheet") return "font-mono text-xs";
  return "text-stone-600 dark:text-stone-300";
}

export interface TextBasedPreviewProps {
  filename: string;
  citedText: string;
  pageNumber?: number | undefined;
  fileType: CitationFileType;
}

export function TextBasedPreview({
  filename,
  citedText,
  pageNumber,
  fileType,
}: TextBasedPreviewProps) {
  const IconComponent = getFileTypeIcon(fileType);
  return (
    <div className="p-3 space-y-3">
      <div className="flex items-center gap-2 text-xs text-stone-500 dark:text-stone-400">
        <IconComponent className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="truncate">{filename}</span>
        {pageNumber && (
          <>
            <span className="text-stone-300 dark:text-stone-600">&bull;</span>
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              Page {pageNumber}
            </span>
          </>
        )}
      </div>
      <div className={cn("relative pl-3 border-l-2", getFileTypeBorderClass(fileType))}>
        <p className={cn("text-sm leading-relaxed line-clamp-4", getFileTypeTextClass(fileType))}>
          &quot;{citedText}&quot;
        </p>
      </div>
    </div>
  );
}

const TEXT_PREVIEW_CONTAINER_HEIGHT = 230;

export interface TextFilePreviewProps {
  fileUrl?: string | undefined;
  filename: string;
  citedText: string;
}

export function TextFilePreview({ fileUrl, filename, citedText }: TextFilePreviewProps) {
  const [content, setContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!fileUrl) {
      setError("No file URL provided");
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    async function loadTextFile() {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(fileUrl!);
        if (!response.ok) throw new Error(`Failed to load file: ${response.statusText}`);
        const text = await response.text();
        if (!cancelled) setContent(text);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load text file");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    loadTextFile();
    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  useEffect(() => {
    if (content && highlightRef.current && containerRef.current) {
      requestAnimationFrame(() => {
        if (highlightRef.current && containerRef.current) {
          const container = containerRef.current;
          const highlight = highlightRef.current;
          const containerRect = container.getBoundingClientRect();
          const highlightRect = highlight.getBoundingClientRect();
          const scrollTop =
            highlight.offsetTop - containerRect.height / 2 + highlightRect.height / 2;
          container.scrollTo({ top: Math.max(0, scrollTop), behavior: "instant" });
        }
      });
    }
  }, [content]);

  if (isLoading) return <PreviewLoading />;
  if (error || !content)
    return <TextBasedPreview filename={filename} citedText={citedText} fileType="text" />;

  const highlightContent = () => {
    if (!citedText) return <span>{content}</span>;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let matchIndex = 0;
    const lowerContent = content.toLowerCase();
    const lowerCited = citedText.toLowerCase();
    let firstHighlightRef = true;
    let searchIndex = 0;
    while (searchIndex < content.length) {
      const foundIndex = lowerContent.indexOf(lowerCited, searchIndex);
      if (foundIndex === -1) break;
      if (foundIndex > lastIndex)
        parts.push(<span key={`text-${matchIndex}`}>{content.slice(lastIndex, foundIndex)}</span>);
      parts.push(
        <span
          key={`highlight-${matchIndex}`}
          ref={firstHighlightRef ? highlightRef : undefined}
          className="bg-primary/30 text-primary-foreground px-0.5 rounded-sm border border-primary/50"
        >
          {content.slice(foundIndex, foundIndex + citedText.length)}
        </span>,
      );
      firstHighlightRef = false;
      lastIndex = foundIndex + citedText.length;
      searchIndex = lastIndex;
      matchIndex++;
    }
    if (lastIndex < content.length)
      parts.push(<span key="text-end">{content.slice(lastIndex)}</span>);
    return parts.length > 0 ? <>{parts}</> : <span>{content}</span>;
  };

  return (
    <div
      className="relative bg-muted/50 flex flex-col"
      style={{ height: TEXT_PREVIEW_CONTAINER_HEIGHT }}
    >
      <div className="flex items-center gap-2 px-3 py-1.5 border-b bg-muted/80 text-xs text-muted-foreground">
        <FileText className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="truncate">{filename}</span>
      </div>
      <div ref={containerRef} className="flex-1 overflow-auto p-3">
        <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-foreground">
          {highlightContent()}
        </pre>
      </div>
    </div>
  );
}
